import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api, errorMessage, isCode } from "../api/client.ts";
import { IconArrow } from "../components/Icons.tsx";
import { Alert, Spinner } from "../components/ui.tsx";
import { fmt, rich, useT } from "../i18n/index.tsx";
import { AuthLayout, panelAccent } from "./AuthLayout.tsx";
import { INVITE_KEY } from "./Invite.tsx";

type Verified = { number?: string; invite_url: string; approved: boolean };

const APPLY_URL = "https://icryptonow.com/apply/";
const SANDBOX_URL = "https://icryptonow.com/sandbox/";
const TELEGRAM_URL = "https://t.me/mrho68";

// One verify call per token per page load: the link is single-use, and React StrictMode runs effects twice in
// development.
const verifies = new Map<string, Promise<Verified>>();
const verifyOnce = (path: string, token: string) => {
  let p = verifies.get(token);
  if (!p) {
    p = api.post<Verified>(path, { token });
    verifies.set(token, p);
  }
  return p;
};

/** "/invite#<token>" from the invite_url the server returns (only the fragment is used, so we stay on this origin). */
function invitePath(inviteUrl: string): string {
  let hash = "";
  try { hash = new URL(inviteUrl, window.location.origin).hash; } catch { hash = ""; }
  return "/invite" + hash;
}

/** Public page the sign-up confirmation e-mail links to: /apply/verify#<token>, or /sandbox/verify#<token> for a
 *  sandbox sign-up (no customer number). */
export function ApplyVerify({ sandbox = false }: { sandbox?: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  // Read the token from the fragment once, then take it out of the address bar (and so out of history).
  const [token] = useState(() => {
    const fromHash = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    if (fromHash) window.history.replaceState(window.history.state, "", window.location.pathname);
    return fromHash;
  });
  const [step, setStep] = useState<"loading" | "done" | "expired">(token ? "loading" : "expired");
  const [result, setResult] = useState<Verified | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    verifyOnce(sandbox ? "/sandbox-signup/verify" : "/apply/verify", token)
      .then((r) => {
        setResult(r);
        setStep("done");
      })
      .catch((e) => {
        setError(isCode(e, "not_found") ? null : errorMessage(e));
        setStep("expired");
      });
  }, [token, sandbox]);

  const next = result ? invitePath(result.invite_url) : null;
  const go = () => {
    if (!next) return;
    // The invite page also reads the fragment, but keep a copy in case a redirect drops it.
    const tok = next.split("#")[1];
    if (tok) { try { sessionStorage.setItem(INVITE_KEY, decodeURIComponent(tok)); } catch { /* ignore */ } }
    navigate(next, { replace: true });
  };

  useEffect(() => {
    if (step !== "done" || !next) return;
    const id = window.setTimeout(go, 2000);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, next]);

  return (
    <AuthLayout panelTitle={rich(t.auth.invitePanelTitle, panelAccent)} panelPoints={t.auth.invitePanelPoints}>
      {step === "loading" && (
        <div className="auth-form center-block"><Spinner label={t.auth.verifyChecking} /></div>
      )}
      {step === "done" && result && (
        <div className="auth-form" role="status">
          <h1>{t.auth.verifyTitle}</h1>
          {result.number && <p className="verify-number">{fmt(t.auth.verifyNumber, { number: result.number })}</p>}
          <p className="lead-sm">{t.auth.verifyNext}</p>
          <button type="button" className="btn btn-primary btn-block" onClick={go} autoFocus>
            {t.common.continue} <IconArrow />
          </button>
          <p className="muted small note-center">{t.auth.verifyRedirect}</p>
        </div>
      )}
      {step === "expired" && (
        <div className="auth-form">
          <h1>{t.auth.verifyExpiredTitle}</h1>
          {error ? <Alert tone="error">{error}</Alert> : <p className="lead-sm">{t.auth.verifyExpiredBody}</p>}
          <a href="/login" className="btn btn-primary btn-block">{t.auth.goToLogIn}</a>
          <a href={sandbox ? SANDBOX_URL : APPLY_URL} className="btn btn-ghost btn-block">{t.auth.applyAgain}</a>
          <p className="muted small note-center">
            {rich(t.vault.guide.help, { a: (c) => <a href={TELEGRAM_URL} target="_blank" rel="noreferrer noopener">{c}</a> })}
          </p>
        </div>
      )}
    </AuthLayout>
  );
}
